import { useEffect, useCallback, useRef } from "react";

import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import useAdblockDetector from "../../hooks/useAdblockDetector.ts";
import useMutationObserver from "../../hooks/useMutationObserver.js";

import { setAdBlock } from "../../redux/reducers/uiReducer.js";

import initGA4 from "../../utils/google-analytics/index.js";
import initGTM from "../../utils/google-tag-manager/index.js";

import { logDebug, logError } from "../../utils/logger.js";
import { equalsIgnoringCase } from "../../utils/validators.js";

function GoogleComponent() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useMutationObserver(
    useRef(document.body),
    () => {
      document.querySelector(".google-revocation-link-placeholder")?.remove();
      document
        .querySelector('div[style*="forced-color-adjust: initial !important;"]')
        ?.remove();
    },
    {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: true,
    }
  );

  const [isAdblockCheckComplete, isAdblockDetected] = useAdblockDetector();

  const adBlockDetected = useCallback(() => {
    // Initialize Google Analytics 4 (Anonymized user)
    initGA4({
      gaOptions: {
        anonymize_ip: true,
        allowAdPersonalizationSignals: false,
      },
    });
    // Initalize Google Tag Manager
    initGTM();
  }, []);

  // Initialize analytics
  const initAnalytics = useCallback(() => {
    // Initialize Google Analytics 4
    initGA4();
    // Initalize Google Tag Manager
    initGTM();
  }, []);

  const adBlockNotDetected = useCallback(() => {
    // No Block
    if (!window.googlefc) {
      logDebug("GoogleComponent", "adBlockNotDetected", {
        msg: "googlefc not available, initializing analytics with defaults",
      });
      initAnalytics();
      return;
    }
    window.googlefc.callbackQueue = window.googlefc.callbackQueue || [];
    window.googlefc.callbackQueue.push({
      CONSENT_DATA_READY: () =>
        window.__tcfapi("addEventListener", 2, (data, success) => {
          if (success) {
            if (data.gdprApplies) {
              switch (data.eventStatus) {
                case "tcloaded":
                case "useractioncomplete":
                  // Initialize analytics
                  initAnalytics();
                  logDebug("GoogleComponent", "adBlockNotDetected", {
                    eventStatus: "useractioncomplete",
                    data: data,
                  });
                  break;
                default:
                  logDebug("GoogleComponent", "adBlockNotDetected", {
                    eventStatus: data.eventStatus,
                    data: data,
                  });
                  break;
              }
            } else {
              // Initialize analytics
              initAnalytics();
            }
          }
        }),
    });
  }, [initAnalytics]);

  useEffect(() => {
    try {
      const cookieName = "FCCDCF";
      let cookieDomain = "";
      if (equalsIgnoringCase(process.env.REACT_APP_ENV, "PREPRODUCTION")) {
        cookieDomain = "ydray.com";
      } else {
        cookieDomain = process.env.REACT_APP_DOMAIN;
      }
      const cookiePath = "/";
      let tc = ("; " + document.cookie).split("; " + cookieName + "=");
      if (tc.length === 2) {
        tc = decodeURIComponent(tc.pop().split(";").shift());
        tc = JSON.parse(tc)[3][0].substring(1, 9);
        tc = Uint8Array.from(window.atob(tc), (v) => v.charCodeAt(0));
        let dt =
          tc[0] * 2 ** 28 +
          tc[1] * 2 ** 20 +
          tc[2] * 2 ** 12 +
          tc[3] * 2 ** 4 +
          (tc[4] >> 4);
        if (Date.now() / 1000 - dt / 10 > 86400 * 365) {
          document.cookie =
            cookieName +
            "=;path=" +
            cookiePath +
            ";domain=" +
            cookieDomain +
            ";expires=" +
            new Date(0).toUTCString();
          logDebug("google-component", "delete FCCDCF cookie", {
            msg: t("message.iab_cookie_expired"),
          });
        } else {
          logDebug("google-component", "delete FCCDCF cookie", {
            msg: t("message.iab_cookie_not_expired"),
          });
        }
      }
    } catch (e) {
      logError("google-component", "delete FCCDCF cookie", {
        error: e,
        msg: t(`errors.${e}`, "errors.default"),
      });
    }
  }, [t]);

  useEffect(() => {
    // Detect if blockAdBlock exists
    if (isAdblockCheckComplete) {
      if (isAdblockDetected) {
        // Ad block detected
        logDebug("google-component", "AdBlockDetect", {
          msg: t("message.adblock_detected"),
        });
        // Adblock detected
        dispatch(setAdBlock(true));
        adBlockDetected();
      } else {
        // Ad block not detected
        logDebug("google-component", "AdBlockDetect", {
          msg: t("message.adblock_not_detected"),
        });
        // Adblock not detected
        dispatch(setAdBlock(false));
        adBlockNotDetected();
      }
    }
  }, [
    isAdblockCheckComplete,
    isAdblockDetected,
    t,
    dispatch,
    adBlockDetected,
    adBlockNotDetected,
  ]);

  return null;
}

export default GoogleComponent;
